import { createReducer, on, Action } from '@ngrx/store';
import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity';

import * as LocationsActions from './locations.actions';
import { LocationInterfaceWithId } from '@sk/sk-interfaces';

export const LOCATIONS_FEATURE_KEY = 'locations';

export interface LocationsState extends EntityState<LocationInterfaceWithId> {
  locationLoaded: boolean; // has the Clients list been loaded
  locationLoadError?: string | null; // last none error (if any)

  queryLocationLoadError?: string | null; // last none error (if any)

  addLocationError?: string | null; // last none error when adding a new client (if any)
  addLocationSuccess: boolean; // last none error when adding a new client (if any)

  updateLocationError?: string | null; // last none error when adding a new client (if any)
  updateLocationSuccess: boolean; // last none error when adding a new client (if any)

  deleteLocationError?: string | null; // last none error when adding a new client (if any)
  deleteLocationSuccess: boolean; // last none error when adding a new client (if any)
}

export interface LocationPartialState {
  readonly [LOCATIONS_FEATURE_KEY]: LocationsState;
}

export const locationsAdapter: EntityAdapter<LocationInterfaceWithId> = createEntityAdapter<LocationInterfaceWithId>({
  selectId: selectLocationsId
});

export const initialState: LocationsState = locationsAdapter.getInitialState({
  // set initial required properties
  locationLoaded: false,
  addLocationSuccess: false,
  updateLocationSuccess: false,
  deleteLocationSuccess: false
});


export function selectLocationsId(a: LocationInterfaceWithId): string {
  //In this case this would be optional since primary key is id
  return a.id;
}


const locationsReducer = createReducer(
  initialState,

  on(LocationsActions.loadLocations, (state) => {
    if (state.locationLoadError) {
      delete state.locationLoadError;
    }
    if (state.addLocationError) {
      delete state.addLocationError;
    }
    if (state.updateLocationError) {
      delete state.updateLocationError;
    }
    if (state.deleteLocationError) {
      delete state.deleteLocationError;
    }

    return ({
      ...state,
      locationLoaded: false
    });
  }),

  on(LocationsActions.loadLocationsSuccess, (state, { locations }) => {
    return {
      ...state,
      locationLoaded: true
    };
  }),

  on(LocationsActions.loadLocationsFailure, (state, { error }) => {
    if (error) {
      console.error(error);
    }
    return ({
      ...state,
      locationLoadError: error
    });
  }),

  // ACTIONS INITIATED FROM THIS CLIENT
  // FIREBASE WILL AUTOMATICALLY UPDATE STATE THROUGH QUERY!!


  /**
   * Add Staff from Client
   */
  on(LocationsActions.addLocation, (state, { location }) => {
    if (state.addLocationError) {
      delete state.addLocationError;
    }
    return ({
      ...state,
      addLocationSuccess: false
    });
  }),

  on(LocationsActions.addLocationError, (state, { error }) => {
    return ({
      ...state,
      addLocationError: error.message
    });
  }),

  on(LocationsActions.addLocationSuccess, (state, {}) => {
    if (state.addLocationError) {
      delete state.addLocationError;
    }
    return ({
      ...state,
      addLocationSuccess: true
    });
  }),

  /**
   * Delete Staff from Client
   */
  on(LocationsActions.removeLocation, (state, {
    id
  }) => {
    if (state.deleteLocationError) {
      delete state.deleteLocationError;
    }
    return locationsAdapter.removeOne(id, {
      ...state,
      deleteLocationSuccess: false
    });
  }),


  on(LocationsActions.removeLocationSuccess, (state, {}) => {
    if (state.deleteLocationError) {
      delete state.deleteLocationError;
    }
    return ({
      ...state,
      deleteLocationSuccess: true
    });
  }),


  on(LocationsActions.removeLocationError, (state, {
    error
  }) => {
    return ({
      ...state,
      deleteLocationError: error.message
    });

  }),


  /**
   * Update staff from Client
   */
  on(LocationsActions.updateLocation, (state, { id, changes }) => {
    if (state.updateLocationError) {
      delete state.updateLocationError;
    }
    return locationsAdapter.updateOne({
        id,
        changes
      },
      {
        ...state,
        updateLocationSuccess: false
      });
  }),

  on(LocationsActions.updateLocationSuccess, (state, {}) => {
    if (state.updateLocationError) {
      delete state.updateLocationError;
    }
    return {
      ...state,
      updateLocationSuccess: true
    };
  }),

  on(LocationsActions.updateLocationsError, (state, { error }) => {
    return ({
      ...state,
      updateLocationError: error.message
    });
  }),


  /**
   * FIRSTORE ACTIONS
   */
  on(LocationsActions.fromFireStoreAddLocation, (state, { location }) => {
    if (state.queryLocationLoadError) {
      delete state.queryLocationLoadError;
    }
    return locationsAdapter.setOne(location, state);
  }),

  on(LocationsActions.fromFireStoreModifyLocation, (state, { location }) => {
    if (state.queryLocationLoadError) {
      delete state.queryLocationLoadError;
    }
    return locationsAdapter.updateOne({
        id: location.id,
        changes: location
      },
      state);
  }),
  on(LocationsActions.fromFireStoreRemoveLocation, (state, {
    id
  }) => {
    if (state.queryLocationLoadError) {
      delete state.queryLocationLoadError;
    }
    return locationsAdapter.removeOne(id, state);
  }),
  on(LocationsActions.queryFireStoreLocationsError, (state, {
    error
  }) => {
    return {
      queryLocationLoadError: error.message,
      ...state
    };

  })
);


export function reducer(state: LocationsState | undefined, action: Action) {
  return locationsReducer(state, action);
}
